import {
  Card,
  CardMedia,
  Typography,
  IconButton,
  Box,
  Alert,
  Snackbar,
  Rating,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FavoriteBorder, Favorite, AccessTime, Star, Speed } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import IRecipe from '../../interfaces/IRecipe';
import { updateUserFavorites } from '../../services/DbService';
import { trackRecipeEvent, trackError } from '../../services/analytics';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../providers/ModalProvider';
import { useTheme } from '../../providers/ThemeProvider';
import { favoritedColor, white, secondary, blackText, straightFont } from '../../Constants';
import { auth } from '../../firebase';
import useRouter from '../../hooks/use-router';
import { getRecipeUrl } from '../../utils/seo';

const difficultyConfig = {
  easy: { label: 'Easy', color: '#4CAF50' },
  medium: { label: 'Medium', color: '#FF9800' },
  hard: { label: 'Hard', color: '#F44336' },
};

interface RecipeCardProps {
  recipe: IRecipe;
}

export default function RecipeCard(props: RecipeCardProps) {
  const { recipe } = props;
  const router = useRouter();
  const { currentUser, setCurrentUser } = useAuth();
  const { setShowLoginModal, setTriggerFavMessage } = useModal();
  const { mode, colors } = useTheme();
  const [isFavorited, setIsFavorited] = useState<boolean>(false);
  const [triggerEmailVerifyMessage, setTriggerEmailVerifyMessage] = useState(false);

  const handleFavoriteClick = async () => {
    if (currentUser && auth.currentUser?.emailVerified) {
      await updateUserFavorites(currentUser, recipe.id);

      const updatedFavorites = currentUser.favorites.includes(recipe.id)
        ? currentUser.favorites.filter((id) => id !== recipe.id)
        : [...currentUser.favorites, recipe.id];

      setCurrentUser({
        ...currentUser,
        favorites: updatedFavorites,
      });

      setIsFavorited((prev) => !prev);

      trackRecipeEvent(
        updatedFavorites.includes(recipe.id) ? 'favorite' : 'unfavorite',
        recipe.id,
        recipe.title
      );
    } else if (currentUser && auth.currentUser?.emailVerified === false) {
      setTriggerEmailVerifyMessage(true);
      trackError('favorite_error', 'Email not verified');
    } else {
      setTriggerFavMessage(true);
      setShowLoginModal(true);
      trackError('favorite_error', 'User not logged in');
    }
  };

  const handleCardClick = () => {
    trackRecipeEvent('view', recipe.id, recipe.title);
    router.push(getRecipeUrl(recipe));
  };

  useEffect(() => {
    if (currentUser && auth.currentUser?.emailVerified) {
      setIsFavorited(currentUser.favorites.includes(recipe.id));
    }
  }, [currentUser, recipe.id]);

  return (
    <motion.div
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      style={{ height: '100%' }}
    >
      <Card
        onClick={handleCardClick}
        sx={{
          width: '100%',
          maxWidth: 380,
          borderRadius: 3,
          boxShadow: mode === 'light' ? '0 2px 12px rgba(0, 0, 0, 0.08)' : '0 2px 12px rgba(0, 0, 0, 0.3)',
          cursor: 'pointer',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: colors.cardBackground,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: colors.border,
          transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
          '&:hover': {
            boxShadow: mode === 'light' ? '0 8px 30px rgba(0, 0, 0, 0.12)' : '0 8px 30px rgba(0, 0, 0, 0.4)',
            borderColor: mode === 'light' ? alpha(blackText, 0.1) : 'rgba(255, 255, 255, 0.15)',
          },
        }}
      >
        {/* Image Container with Overlay Heart */}
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            image={recipe.image}
            alt={recipe.title}
            sx={{
              aspectRatio: '4/3',
              objectFit: 'cover',
            }}
          />
          {/* Difficulty Badge Overlay */}
          {recipe.difficulty && (
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: alpha(difficultyConfig[recipe.difficulty].color, 0.95),
                color: white,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backdropFilter: 'blur(4px)',
              }}
            >
              <Speed sx={{ fontSize: 14 }} />
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontFamily: straightFont,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {difficultyConfig[recipe.difficulty].label}
              </Typography>
            </Box>
          )}

          {/* Favorite Button Overlay */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleFavoriteClick();
            }}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              backgroundColor: alpha(white, 0.9),
              backdropFilter: 'blur(4px)',
              color: isFavorited ? favoritedColor : alpha(blackText, 0.4),
              width: 40,
              height: 40,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: white,
                transform: 'scale(1.1)',
              },
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={isFavorited ? 'favorited' : 'unfavorited'}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ duration: 0.15 }}
                style={{ display: 'flex' }}
              >
                {isFavorited ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
              </motion.div>
            </AnimatePresence>
          </IconButton>
        </Box>

        {/* Content - Minimal and Clean */}
        <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              fontFamily: straightFont,
              fontWeight: 600,
              fontSize: '1.1rem',
              lineHeight: 1.3,
              color: colors.text,
              mb: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {recipe.title}
          </Typography>

          {/* Meta Row - Time and Rating */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: 'auto',
            }}
          >
            {/* Cook Time */}
            {(recipe.totalTime || recipe.cookTime) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTime sx={{ fontSize: 18, color: colors.textSecondary }} />
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: colors.textSecondary,
                    fontFamily: straightFont,
                  }}
                >
                  {recipe.totalTime || recipe.cookTime}
                </Typography>
              </Box>
            )}

            {/* Rating */}
            {recipe.averageRating > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Rating
                  value={recipe.averageRating}
                  precision={0.5}
                  size="small"
                  readOnly
                  icon={<Star fontSize="inherit" sx={{ color: secondary }} />}
                  emptyIcon={<Star fontSize="inherit" sx={{ color: mode === 'light' ? alpha(blackText, 0.2) : 'rgba(255,255,255,0.2)' }} />}
                />
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    color: colors.textSecondary,
                    fontFamily: straightFont,
                  }}
                >
                  ({recipe.commentsWithRatings || 0})
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Email Verify Snackbar */}
        {triggerEmailVerifyMessage && (
          <Snackbar
            open={triggerEmailVerifyMessage}
            autoHideDuration={3000}
            onClose={() => setTriggerEmailVerifyMessage(false)}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setTriggerEmailVerifyMessage(false)}
              >
                <CloseIcon />
              </IconButton>
            }
          >
            <Alert onClose={() => setTriggerEmailVerifyMessage(false)} severity="error">
              You must verify your email before favoriting/commenting
            </Alert>
          </Snackbar>
        )}
      </Card>
    </motion.div>
  );
}
